import { Button } from "@/components/core/buttons/button";
import {
	Form,
	FormDescription,
	FormField,
	FormGroup,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import {
	type PasswordFormSchema,
	passwordFormSchema,
} from "@/features/dashboard/types/user.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

export default function UserProfilePasswordForm({
	data,
	onSubmit,
}: {
	data?: PasswordFormSchema;
	onSubmit: (data: PasswordFormSchema, reset: () => void) => void;
}) {
	const form = useForm<PasswordFormSchema>({
		resolver: zodResolver(passwordFormSchema),
		defaultValues: {
			id: data?.id,
			current_password: "",
			password: "",
			password_confirmation: "",
		},
	});

	return (
		<Form {...form}>
			<form
				className="w-full mt-2"
				onSubmit={form.handleSubmit((data) => onSubmit(data, form.reset))}
			>
				<div className="flex flex-col w-full gap-6">
					<div className="flex flex-row items-center justify-between w-full gap-4">
						<div className="inline-flex flex-col w-auto">
							<span className="text-xl text-foreground-dark font-semibold">
								Change Password
							</span>
							<span className="text-base text-foreground/70">
								Reset or update your password. It is recommended that you update
								your password regularly to improve and maintain account
								security. Regular password changes help protect against illegal
								access and keep the account safe.
							</span>
						</div>
					</div>
					<div className="flex flex-col w-full gap-4">
						<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
							<div className="col-span-12 w-full">
								<FormField
									control={form.control}
									name="current_password"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Old Password</FormLabel>
												<FormDescription />
											</div>
											<FormGroup iconPosition="left">
												<Input type="password" autoComplete="off" {...field} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-12 w-full">
								<FormField
									control={form.control}
									name="password"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>New Password</FormLabel>
												<FormDescription />
											</div>
											<FormGroup iconPosition="left">
												<Input type="password" autoComplete="off" {...field} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-12 w-full">
								<FormField
									control={form.control}
									name="password_confirmation"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Confirm Password</FormLabel>
												<FormDescription />
											</div>
											<FormGroup iconPosition="left">
												<Input type="password" autoComplete="off" {...field} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
						<div className="flex flex-row w-full mt-2">
							<Button type="submit">Save Changes</Button>
						</div>
					</div>
				</div>
			</form>
		</Form>
	);
}
